/**
 * Catalog Mode's "Enquire Now" modal — a single shared instance for the
 * whole page (see craftis_catalog_mode_render_enquiry_modal() in
 * inc/catalog-mode.php), opened by any [data-craftis-enquire-trigger]
 * button (grid cards, single-product buy box, including ones rendered
 * later via AJAX — the click listener is delegated on document, not
 * bound per-button, so new cards need no re-init).
 *
 * Validates client-side (required fields, email format, mobile digit
 * count, positive integer quantity), then builds either a wa.me deep
 * link or a mailto: link from the same field values and navigates to
 * it — there is no backend enquiry-submission endpoint by design: the
 * "submission" IS handing off to WhatsApp/the shopper's email client,
 * per the spec's "generate the message, open WhatsApp/mailto:" requirement.
 *
 * Config (WhatsApp number, enquiry email, message templates) comes from
 * window.craftisCatalogModeL10n, localized server-side in
 * craftis_catalog_mode_localize_script() (inc/catalog-mode.php) so
 * nothing here is hardcoded.
 */

( function () {
	'use strict';

	var MOBILE_MIN_DIGITS = 7;
	var MOBILE_MAX_DIGITS = 15;
	var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	var modal = null;
	var els = {};
	var isSubmitting = false;
	var lastFocusedEl = null;

	function config() {
		return window.craftisCatalogModeL10n || {};
	}

	function t( key, fallback ) {
		var i18n = config().i18n || {};
		return i18n[ key ] || fallback;
	}

	function cacheEls() {
		modal = document.querySelector( '[data-craftis-enquiry-modal]' );

		if ( ! modal ) {
			return false;
		}

		els.overlay = modal.querySelector( '[data-craftis-enquiry-overlay]' );
		els.closeBtn = modal.querySelector( '[data-craftis-enquiry-close]' );
		els.form = modal.querySelector( '[data-craftis-enquiry-form]' );
		els.productDisplay = modal.querySelector( '[data-craftis-enquiry-product-display]' );
		els.formError = modal.querySelector( '[data-craftis-enquiry-form-error]' );
		els.fields = {};

		modal.querySelectorAll( '[data-craftis-enquiry-field]' ).forEach( function ( field ) {
			els.fields[ field.dataset.craftisEnquiryField ] = field;
		} );

		els.submitButtons = modal.querySelectorAll( '[data-craftis-enquiry-submit]' );

		return true;
	}

	function openModal( product ) {
		if ( ! modal ) {
			return;
		}

		lastFocusedEl = document.activeElement;

		resetForm();

		if ( els.fields.product_id ) {
			els.fields.product_id.value = product.id || '';
		}
		if ( els.fields.product_name ) {
			els.fields.product_name.value = product.name || '';
		}
		if ( els.productDisplay ) {
			els.productDisplay.textContent = product.name || '';
		}

		modal.classList.add( 'is-open' );
		modal.setAttribute( 'aria-hidden', 'false' );
		document.body.style.overflow = 'hidden';

		var firstField = modal.querySelector( '#craftis-enquiry-name' );
		if ( firstField ) {
			firstField.focus();
		}
	}

	function closeModal() {
		if ( ! modal ) {
			return;
		}

		modal.classList.remove( 'is-open' );
		modal.setAttribute( 'aria-hidden', 'true' );
		document.body.style.overflow = '';

		if ( lastFocusedEl && typeof lastFocusedEl.focus === 'function' ) {
			lastFocusedEl.focus();
		}
	}

	function resetForm() {
		if ( els.form ) {
			els.form.reset();
		}

		if ( els.fields.quantity ) {
			els.fields.quantity.value = '1';
		}

		clearErrors();
		setSubmitting( false );
	}

	function clearErrors() {
		modal.querySelectorAll( '[data-craftis-enquiry-error]' ).forEach( function ( el ) {
			el.textContent = '';
		} );

		if ( els.formError ) {
			els.formError.hidden = true;
			els.formError.textContent = '';
		}
	}

	function showFieldError( fieldName, message ) {
		var errorEl = modal.querySelector( '[data-craftis-enquiry-error="' + fieldName + '"]' );
		if ( errorEl ) {
			errorEl.textContent = message;
		}
	}

	/**
	 * @return {Object|null} Trimmed field values, or null if validation failed
	 *                        (individual field errors are already shown by this point).
	 */
	function validate() {
		clearErrors();

		var values = {
			name: ( els.fields.name && els.fields.name.value.trim() ) || '',
			email: ( els.fields.email && els.fields.email.value.trim() ) || '',
			mobile: ( els.fields.mobile && els.fields.mobile.value.trim() ) || '',
			address: ( els.fields.address && els.fields.address.value.trim() ) || '',
			quantity: ( els.fields.quantity && els.fields.quantity.value.trim() ) || '',
			productId: ( els.fields.product_id && els.fields.product_id.value ) || '',
			productName: ( els.fields.product_name && els.fields.product_name.value ) || '',
		};

		var isValid = true;
		var requiredMsg = t( 'required', 'This field is required.' );

		if ( ! values.name ) {
			showFieldError( 'name', requiredMsg );
			isValid = false;
		}

		if ( ! values.email ) {
			showFieldError( 'email', requiredMsg );
			isValid = false;
		} else if ( ! EMAIL_PATTERN.test( values.email ) ) {
			showFieldError( 'email', t( 'invalidEmail', 'Please enter a valid email address.' ) );
			isValid = false;
		}

		var mobileDigits = values.mobile.replace( /[^0-9]/g, '' );
		if ( ! values.mobile ) {
			showFieldError( 'mobile', requiredMsg );
			isValid = false;
		} else if ( mobileDigits.length < MOBILE_MIN_DIGITS || mobileDigits.length > MOBILE_MAX_DIGITS ) {
			showFieldError( 'mobile', t( 'invalidMobile', 'Please enter a valid mobile number.' ) );
			isValid = false;
		}

		if ( ! values.address ) {
			showFieldError( 'address', requiredMsg );
			isValid = false;
		}

		var quantityNum = parseInt( values.quantity, 10 );
		if ( ! values.quantity || isNaN( quantityNum ) || quantityNum <= 0 ) {
			showFieldError( 'quantity', t( 'invalidQty', 'Quantity must be a positive number.' ) );
			isValid = false;
		}

		if ( ! isValid ) {
			return null;
		}

		values.quantity = String( quantityNum );

		return values;
	}

	function setSubmitting( state ) {
		isSubmitting = state;

		els.submitButtons.forEach( function ( btn ) {
			btn.disabled = state;
		} );
	}

	/**
	 * @param {string} template printf-style template with %s placeholders.
	 * @param {Array<string>} args Replacement values, in order.
	 * @return {string}
	 */
	function formatTemplate( template, args ) {
		var i = 0;
		return template.replace( /%s/g, function () {
			return args[ i++ ] || '';
		} );
	}

	function submitWhatsApp( values ) {
		var number = config().whatsappNumber || '';

		if ( ! number ) {
			return;
		}

		var template = t( 'whatsappBody', 'New product enquiry\n\nProduct: %s\nQuantity: %s\n\nName: %s\nEmail: %s\nMobile: %s\nAddress: %s' );
		var message = formatTemplate( template, [
			values.productName,
			values.quantity,
			values.name,
			values.email,
			values.mobile,
			values.address,
		] );

		var url = 'https://wa.me/' + number + '?text=' + encodeURIComponent( message );

		window.open( url, '_blank', 'noopener' );

		finishSubmit();
	}

	function submitEmail( values ) {
		var to = config().enquiryEmail || '';

		var subjectTemplate = t( 'emailSubject', 'Product Enquiry: %s' );
		var subject = formatTemplate( subjectTemplate, [ values.productName ] );

		var bodyTemplate = t( 'emailBody', 'Product: %s\nQuantity: %s\n\nName: %s\nEmail: %s\nMobile: %s\nAddress: %s' );
		var body = formatTemplate( bodyTemplate, [
			values.productName,
			values.quantity,
			values.name,
			values.email,
			values.mobile,
			values.address,
		] );

		var url = 'mailto:' + encodeURIComponent( to )
			+ '?subject=' + encodeURIComponent( subject )
			+ '&body=' + encodeURIComponent( body );

		window.location.href = url;

		finishSubmit();
	}

	function finishSubmit() {
		setSubmitting( false );
		closeModal();
	}

	function handleSubmitClick( method ) {
		if ( isSubmitting ) {
			return;
		}

		var values = validate();

		if ( ! values ) {
			return;
		}

		setSubmitting( true );

		if ( 'whatsapp' === method ) {
			submitWhatsApp( values );
		} else {
			submitEmail( values );
		}
	}

	/**
	 * When the Enquire button sits inside a variation form (single-product
	 * buy box for a variable product — see
	 * craftis_catalog_mode_variation_enquire_button() in inc/catalog-mode.php,
	 * which keeps WooCommerce's own type/color <select> dropdowns and
	 * only swaps the Add to Cart button itself), reads which variation is
	 * currently selected straight from the DOM: the human-readable label
	 * WooCommerce's variation.js already put in each <select>'s selected
	 * <option> text, plus the variation_id hidden input it fills in once
	 * the combination is complete. No PHP-side variation lookup is
	 * needed — this mirrors what the shopper is already looking at.
	 *
	 * @param {HTMLElement} trigger The clicked Enquire button.
	 * @return {{variationId: string, variationLabel: string}}
	 */
	function readSelectedVariation( trigger ) {
		var form = trigger.closest( '.variations_form' );

		if ( ! form ) {
			return { variationId: '', variationLabel: '' };
		}

		var variationIdInput = form.querySelector( 'input[name="variation_id"], input.variation_id' );
		var variationId = ( variationIdInput && variationIdInput.value ) || '';

		var labels = [];
		form.querySelectorAll( 'select[name^="attribute_"]' ).forEach( function ( select ) {
			var selectedOption = select.options[ select.selectedIndex ];
			if ( selectedOption && selectedOption.value ) {
				labels.push( selectedOption.textContent.trim() );
			}
		} );

		return {
			variationId: variationId,
			variationLabel: labels.join( ', ' ),
		};
	}

	function bindEvents() {
		document.addEventListener( 'click', function ( e ) {
			var trigger = e.target.closest( '[data-craftis-enquire-trigger]' );

			if ( trigger ) {
				e.preventDefault();

				if ( trigger.disabled ) {
					return;
				}

				var variation = readSelectedVariation( trigger );
				var productName = trigger.dataset.craftisEnquireProductName;

				if ( variation.variationLabel ) {
					productName += ' (' + variation.variationLabel + ')';
				}

				openModal( {
					id: trigger.dataset.craftisEnquireProductId,
					name: productName,
				} );
			}
		} );

		if ( els.closeBtn ) {
			els.closeBtn.addEventListener( 'click', closeModal );
		}

		if ( els.overlay ) {
			els.overlay.addEventListener( 'click', closeModal );
		}

		document.addEventListener( 'keydown', function ( e ) {
			if ( 'Escape' === e.key && modal && modal.classList.contains( 'is-open' ) ) {
				closeModal();
			}
		} );

		if ( els.form ) {
			els.form.addEventListener( 'submit', function ( e ) {
				e.preventDefault();
			} );
		}

		els.submitButtons.forEach( function ( btn ) {
			btn.addEventListener( 'click', function () {
				handleSubmitClick( btn.dataset.craftisEnquirySubmit );
			} );
		} );
	}

	/**
	 * Enables/disables every .craftis-enquire-btn--needs-variation button
	 * on the page as WooCommerce's own variation.js reports a complete,
	 * purchasable variation being selected or not — listens for the same
	 * found_variation / hide_variation / reset_data events variation.js
	 * fires on .variations_form, the same signal a real Add to Cart
	 * button relies on (see .single_add_to_cart_button.disabled in core),
	 * so "choose a variation, then enquire" is enforced the same way
	 * "choose a variation, then buy" normally would be.
	 */
	function bindVariationFormEvents() {
		if ( ! window.jQuery ) {
			return;
		}

		var $ = window.jQuery;

		// WooCommerce's variation.js is built entirely on jQuery and
		// passes the selected variation object as an EXTRA argument to
		// trigger('found_variation', [variation]) — that calling
		// convention (arguments after the event object) is jQuery-specific
		// and never reaches a plain addEventListener callback, so these
		// three events are bound via jQuery itself rather than native DOM
		// listeners, unlike every other event in this file.
		$( '.variations_form' ).each( function () {
			var $form = $( this );
			var btn = this.querySelector( '.craftis-enquire-btn--needs-variation' );

			if ( ! btn ) {
				return;
			}

			$form.on( 'found_variation', function ( e, variation ) {
				btn.disabled = ! ( variation && variation.is_purchasable !== false );
			} );

			$form.on( 'hide_variation reset_data', function () {
				btn.disabled = true;
			} );
		} );
	}

	function init() {
		if ( ! cacheEls() ) {
			return;
		}

		bindEvents();
		bindVariationFormEvents();
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
